'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Link from 'next/link';
import { ArrowLeft, Copy, Download, Check, FileUp, BarChart, PieChart, LineChart } from 'lucide-react';

export default function ChartRecommenderPage() {
  const [csvInput, setCsvInput] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const recommendChart = () => {
    try {
      setError('');
      if (!csvInput.trim()) {
        setError('Please enter CSV data');
        return;
      }

      const lines = csvInput.trim().split('\n');
      if (lines.length < 2) {
        setError('CSV must have at least a header row and one data row');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim());
      const numColumns = headers.length;
      
      let rec = `Based on your data with ${numColumns} columns, we recommend:\n\n`;
      
      if (numColumns === 1) {
        rec += "1. Pie Chart: Great for showing proportions of a single category.\n";
        rec += "2. Bar Chart: Good for comparing values within a single category.";
      } else if (numColumns === 2) {
        rec += "1. Bar Chart: Ideal for comparing values across categories.\n";
        rec += "2. Line Chart: Perfect if one column is time-based.\n";
        rec += "3. Scatter Plot: Useful for showing the relationship between two variables.";
      } else {
        rec += "1. Grouped Bar Chart: Best for comparing multiple series.\n";
        rec += "2. Multi-line Chart: Excellent for tracking several trends over time.\n";
        rec += "3. Stacked Area Chart: Good for showing part-to-whole relationships over time.";
      }

      setRecommendation(rec);
    } catch (err) {
      setError('Error analyzing CSV. Please check your input format.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(recommendation);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvInput(text);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                AI Chart Recommender
              </h1>
            <p className="text-lg text-muted-foreground">
              Get the best chart suggestions for your data instantly
            </p>
          </div>

          {/* File Upload Area */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Upload CSV File</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              <FileUp className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <p className="text-lg font-medium mb-2">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .csv and .txt files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">CSV Input</label>
                <Textarea
                  placeholder="Month,Sales&#10;Jan,100&#10;Feb,150&#10;Mar,200"
                  value={csvInput}
                  onChange={(e) => setCsvInput(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <Button 
                onClick={recommendChart} 
                className="w-full"
                size="lg"
              >
                Get Recommendations
              </Button>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Recommendations</label>
                <Textarea
                  value={recommendation}
                  readOnly
                  placeholder="Your chart recommendations will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-muted"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={copyToClipboard} 
                  className="flex-1"
                  variant="outline"
                  disabled={!recommendation}
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>

          {/* Visual Icons */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="p-4 bg-card rounded-lg border flex flex-col items-center justify-center text-center">
              <BarChart className="w-8 h-8 mb-2 text-blue-500" />
              <span className="text-sm font-medium">Bar Charts</span>
            </div>
            <div className="p-4 bg-card rounded-lg border flex flex-col items-center justify-center text-center">
              <LineChart className="w-8 h-8 mb-2 text-purple-500" />
              <span className="text-sm font-medium">Line Charts</span>
            </div>
            <div className="p-4 bg-card rounded-lg border flex flex-col items-center justify-center text-center">
              <PieChart className="w-8 h-8 mb-2 text-green-500" />
              <span className="text-sm font-medium">Pie Charts</span>
            </div>
          </div>
        </div>
      </main>

      {/* User Problems & FAQs for SEO */}
        <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold mb-4">Common Chart Recommendation Questions</h2>
          <div className="space-y-6">
            <div><p className="font-semibold">1. How does the chart recommender work?</p><p className="text-muted-foreground">It analyzes the structure of your CSV data, such as the number of columns and data types, to suggest the most effective visualizations.</p></div>
            <div><p className="font-semibold">2. What is the best chart for time-series data?</p><p className="text-muted-foreground">Line charts are generally best for showing trends over time.</p></div>
            <div><p className="font-semibold">3. When should I use a pie chart?</p><p className="text-muted-foreground">Pie charts are best for showing parts of a whole when you have a small number of categories.</p></div>
            <div><p className="font-semibold">4. Can I use this tool for free?</p><p className="text-muted-foreground">Yes, this online chart recommender is completely free to use.</p></div>
          </div>
        </section>
      <Footer />
    </div>
  );
}
